import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Send, CheckCircle, XCircle, AlertTriangle, Eye, EyeOff, Server } from 'lucide-react';

const SmtpSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testEmail, setTestEmail] = useState('');
  const [testMessage, setTestMessage] = useState({ type: '', text: '' });

  const [settings, setSettings] = useState({
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPass: '',
    emailFrom: '',
    emailFromName: 'نظام فحص المقبلين على الزواج'
  });

  const [status, setStatus] = useState({
    isConfigured: false,
    hasPassword: false
  });

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/settings/smtp');

      if (response.data.success) {
        const data = response.data.data;
        setSettings({
          smtpHost: data.smtpHost || '',
          smtpPort: data.smtpPort || 587,
          smtpSecure: data.smtpSecure || false,
          smtpUser: data.smtpUser || '',
          smtpPass: '', // Never load password from server
          emailFrom: data.emailFrom || '',
          emailFromName: data.emailFromName || 'نظام فحص المقبلين على الزواج'
        });

        setStatus({
          isConfigured: data.isConfigured,
          hasPassword: data.hasPassword
        });
      }
    } catch (error) {
      console.error('Error loading SMTP settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'حدث خطأ أثناء تحميل الإعدادات'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Validation
      if (!settings.smtpHost || !settings.smtpPort || !settings.smtpUser || !settings.emailFrom) {
        setMessage({
          type: 'error',
          text: 'يرجى ملء جميع الحقول المطلوبة'
        });
        return;
      }

      const response = await axios.post('/admin/settings/smtp', settings);

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'تم حفظ إعدادات البريد الإلكتروني بنجاح'
        });

        // Reload settings to get updated status
        await loadSettings();

        // Clear password field after successful save
        setSettings(prev => ({ ...prev, smtpPass: '' }));
      }
    } catch (error) {
      console.error('Error saving SMTP settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'حدث خطأ أثناء حفظ الإعدادات'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async (e) => {
    e.preventDefault();

    if (!testEmail) {
      setTestMessage({
        type: 'error',
        text: 'يرجى إدخال البريد الإلكتروني للاختبار'
      });
      return;
    }

    try {
      setTesting(true);
      setTestMessage({ type: '', text: '' });

      const response = await axios.post('/admin/settings/smtp/test', {
        testEmail
      });

      if (response.data.success) {
        setTestMessage({
          type: 'success',
          text: `تم إرسال بريد تجريبي بنجاح إلى ${testEmail}. يرجى التحقق من صندوق الوارد الخاص بك.`
        });
      }
    } catch (error) {
      console.error('Error testing SMTP:', error);
      setTestMessage({
        type: 'error',
        text: error.response?.data?.message || 'فشل إرسال البريد التجريبي'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="w-8 h-8 text-blue-600" />
          إعدادات البريد الإلكتروني (SMTP)
        </h1>
        <p className="mt-2 text-gray-600">
          قم بتكوين خادم SMTP لإرسال رسائل البريد الإلكتروني (إعادة تعيين كلمة المرور، الإشعارات)
        </p>
      </div>

      {/* Status Card */}
      <div className={`mb-6 p-4 rounded-lg border ${
        status.isConfigured
          ? 'bg-green-50 border-green-200'
          : 'bg-yellow-50 border-yellow-200'
      }`}>
        <div className="flex items-center gap-3">
          {status.isConfigured ? (
            <>
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-900">خدمة البريد الإلكتروني مفعّلة</p>
                <p className="text-sm text-green-700">يمكن الآن إرسال رسائل البريد الإلكتروني</p>
              </div>
            </>
          ) : (
            <>
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-900">خدمة البريد الإلكتروني غير مفعّلة</p>
                <p className="text-sm text-yellow-700">يرجى تكوين الإعدادات أدناه لتفعيل البريد الإلكتروني</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Success/Error Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-800'
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Main Settings Form */}
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Server className="w-5 h-5 text-blue-600" />
          إعدادات خادم SMTP
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* SMTP Host */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              خادم SMTP <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="smtpHost"
              value={settings.smtpHost}
              onChange={handleChange}
              placeholder="smtp.gmail.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              مثال: smtp.gmail.com, smtp.mailgun.org, smtp.sendgrid.net
            </p>
          </div>

          {/* SMTP Port */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              المنفذ <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="smtpPort"
              value={settings.smtpPort}
              onChange={handleChange}
              placeholder="587"
              min="1"
              max="65535"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              الافتراضي: 587 (TLS) أو 465 (SSL)
            </p>
          </div>

          {/* SMTP Secure */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              اتصال آمن (SSL)
            </label>
            <div className="flex items-center gap-3 mt-3">
              <input
                type="checkbox"
                name="smtpSecure"
                checked={settings.smtpSecure}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">
                استخدم SSL (للمنفذ 465)
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              قم بالتفعيل للمنفذ 465، وعطّل للمنفذ 587
            </p>
          </div>

          {/* SMTP User */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              اسم المستخدم / البريد الإلكتروني <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="smtpUser"
              value={settings.smtpUser}
              onChange={handleChange}
              placeholder="your-email@gmail.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
              dir="ltr"
            />
            <p className="mt-1 text-xs text-gray-500">
              البريد الإلكتروني المستخدم للمصادقة
            </p>
          </div>

          {/* SMTP Password */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              كلمة المرور / App Password {!status.hasPassword && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="smtpPass"
                value={settings.smtpPass}
                onChange={handleChange}
                placeholder={status.hasPassword ? 'اتركه فارغًا للإبقاء على كلمة المرور الحالية' : 'أدخل كلمة المرور'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 pr-10"
                required={!status.hasPassword}
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {status.hasPassword && (
              <p className="mt-1 text-xs text-green-600">
                ✓ كلمة المرور محفوظة (اتركه فارغًا للإبقاء عليها)
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              لـ Gmail: استخدم App Password وليس كلمة المرور العادية
            </p>
          </div>

          {/* Email From */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              البريد الإلكتروني للمرسل <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="emailFrom"
              value={settings.emailFrom}
              onChange={handleChange}
              placeholder="noreply@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
              dir="ltr"
            />
            <p className="mt-1 text-xs text-gray-500">
              البريد الإلكتروني الذي سيظهر كمرسل للرسائل
            </p>
          </div>

          {/* Email From Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              اسم المرسل
            </label>
            <input
              type="text"
              name="emailFromName"
              value={settings.emailFromName}
              onChange={handleChange}
              placeholder="نظام فحص المقبلين على الزواج"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              الاسم الذي سيظهر في صندوق الوارد
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                جاري الحفظ...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                حفظ الإعدادات
              </>
            )}
          </button>
        </div>
      </form>

      {/* Test Email Section */}
      {status.isConfigured && (
        <div className="bg-white shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            اختبار إعدادات البريد الإلكتروني
          </h2>

          {testMessage.text && (
            <div className={`mb-4 p-4 rounded-lg ${
              testMessage.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-center gap-2">
                {testMessage.type === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                <span>{testMessage.text}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleTestEmail} className="flex gap-3">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="أدخل البريد الإلكتروني للاختبار"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
              dir="ltr"
            />
            <button
              type="submit"
              disabled={testing}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {testing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  جاري الإرسال...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  إرسال بريد تجريبي
                </>
              )}
            </button>
          </form>

          <p className="mt-3 text-sm text-gray-600">
            سيتم إرسال بريد تجريبي إلى العنوان المحدد للتحقق من صحة الإعدادات
          </p>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-md font-semibold text-blue-900 mb-3">💡 إرشادات Gmail</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
          <li>قم بتفعيل التحقق بخطوتين على حسابك في Google</li>
          <li>انتقل إلى: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">https://myaccount.google.com/apppasswords</a></li>
          <li>اختر "Mail" و "Other (Custom name)"</li>
          <li>قم بإنشاء App Password (16 حرف)</li>
          <li>استخدم App Password في حقل كلمة المرور أعلاه (وليس كلمة المرور العادية)</li>
        </ol>
        <div className="mt-4 pt-4 border-t border-blue-300">
          <h4 className="font-semibold text-blue-900 mb-2">مزودو خدمة بديلون:</h4>
          <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
            <li><strong>Mailgun:</strong> smtp.mailgun.org (المنفذ 587)</li>
            <li><strong>SendGrid:</strong> smtp.sendgrid.net (المنفذ 587)</li>
            <li><strong>AWS SES:</strong> email-smtp.[region].amazonaws.com (المنفذ 587)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SmtpSettings;
